/* eslint-disable max-lines, max-lines-per-function, max-params */
import { api } from "jiffy-api";
import { loadStripe } from "@stripe/stripe-js";
import { sendDataLayer, track } from "jiffy-analytics";
import { trackCheckoutStepCompleted } from "common/utils/rudderstack/tracker";
import { objNamesToCamelCase } from "common/utils/helpers/cases";
import { redirectTo } from "common/utils/browser";
import paymentOverlay from "components/checkout_v2/payment-overlay/payment-overlay";
import { getBusinessLinePath } from "components/checkout_v2/checkout-config";
import { logCheckoutData, getDeliveryDates } from "./threads-helper";

const startExpressCheckout = async (event, elements, getContext) => {
  paymentOverlay.show();

  const {
    pageSource,
    totalAmountInCents,
    countryCode,
    stripeLineItems,
    stripeShippingRates,
    orderNumber
  } = getContext();

  window.mixpanel && track("checkout_started", { which_checkout: event.expressPaymentType });
  logCheckoutData(
    orderNumber,
    { page: pageSource },
    `${event.expressPaymentType}.checkout_started`
  );
  trackCheckoutStepCompleted("order_checkout", "express_checkout_button_click");

  elements.update({
    amount: totalAmountInCents
  });

  const options = {
    phoneNumberRequired: true,
    emailRequired: true,
    billingAddressRequired: true,
    shippingAddressRequired: true,
    allowedShippingCountries: [countryCode],
    lineItems: stripeLineItems,
    shippingRates: stripeShippingRates
  };

  event.resolve(options);
};

const handleShippingAddressChange = async (event, elements, getContext) => {
  const { resolve, reject, address } = event;
  const { orderNumber } = getContext();

  const payload = {
    city: address.city,
    zipcode: address.postal_code,
    jiffy_classics_order: true,
    payment_method_type: "card"
  };

  try {
    const response = await api.productCollections.updateExpressCheckoutAddress(
      { order_number: orderNumber },
      payload
    );
    const expressCheckoutResponse = objNamesToCamelCase(response.express_checkout);

    elements.update({
      amount: expressCheckoutResponse.totalAmountInCents
    });

    const options = {
      lineItems: expressCheckoutResponse.stripeLineItems,
      shippingRates: expressCheckoutResponse.stripeShippingRates
    };

    resolve(options);
  } catch (error) {
    logCheckoutData(
      orderNumber,
      error,
      `${window.jiffyThreads.payments.selectedPaymentMethod}.shipping_address_error`
    );
    reject(error);
  }
};

const stripeCreatePaymentIntent = async (stripe, elements, paymentData, getContext) => {
  const { orderNumber } = getContext();
  try {
    const intentResponse = await api.jiffyshirts.checkout.stripeCreateIntent({
      ...paymentData,
      business_line_path: getBusinessLinePath()
    });
    const { error: backendError, redirect_to: redirectUrl } = intentResponse;

    if (redirectUrl) {
      redirectTo(redirectUrl);
    } else if (backendError) {
      throw backendError;
    }

    elements.update({ customerSessionClientSecret: intentResponse.client_secret });

    const confirmResponse = await stripe.confirmPayment({
      elements,
      clientSecret: intentResponse.client_secret,
      confirmParams: { return_url: intentResponse.return_url },
      redirect: "always"
    });

    if (confirmResponse.error) {
      throw confirmResponse.error;
    }

    return confirmResponse;
  } catch (error) {
    logCheckoutData(
      orderNumber,
      error,
      `${window.jiffyThreads.payments.selectedPaymentMethod}.create_payment_error`
    );
    paymentOverlay.hide();
    throw error;
  }
};

const handleConfirm = async (event, stripe, elements, getContext) => {
  const { pageSource, userType, csrfToken, orderNumber } = getContext();

  window.mixpanel &&
    track("checkout_confirmed", {
      which_checkout: window.jiffyThreads.payments.selectedPaymentMethod
    });
  logCheckoutData(
    orderNumber,
    { page: pageSource },
    `${window.jiffyThreads.payments.selectedPaymentMethod}.checkout_confirmed`
  );

  sendDataLayer({
    genericEvent: true,
    eventCategory: `Page: ${pageSource}`,
    event: `Confirm: checkout: ${window.jiffyThreads.payments.selectedPaymentMethod}`,
    eventLabel: userType
  });

  const paymentData = {
    payment_method_type: "card",
    authenticity_token: csrfToken,
    checkout_no_confirmation: true,
    jiffy_classics_order: true,
    order: {
      use_store_credits: false,
      bill_address_attributes: event.billingDetails,
      ship_address_attributes: event.shippingAddress
    },
    order_number: orderNumber
  };

  paymentData[window.jiffyThreads.payments.selectedPaymentMethod] = true;

  try {
    if (getBusinessLinePath() !== "made") {
      await getDeliveryDates(orderNumber);
    }
    await stripeCreatePaymentIntent(stripe, elements, paymentData, getContext);
  } catch (error) {
    window.Honeybadger?.notify("[product-collections] Error to create express checkout order", {
      params: { error: JSON.stringify(error) }
    });
    logCheckoutData(
      orderNumber,
      error,
      `${window.jiffyThreads.payments.selectedPaymentMethod}.create_order_error`
    );
    throw error;
  }
};

window.jiffyThreads = window.jiffyThreads || {};
window.jiffyThreads.payments = window.jiffyThreads.payments || {};
window.jiffyThreads.payments.loadStripeButtons = async (mountElement, getContext) => {
  if (!window.PaymentRequest) return;
  const {
    buttonHeight,
    publishableKey,
    currency,
    locale,
    totalAmountInCents,
    pageSource,
    userType,
    orderNumber,
    stripeLayout
  } = getContext();

  const stripe = await loadStripe(publishableKey);
  const elements = stripe.elements({
    mode: "payment",
    currency: currency.toLowerCase(),
    locale,
    amount: totalAmountInCents,
    captureMethod: "manual",
    loader: "always",
    appearance: {
      theme: "stripe"
    }
  });

  const expressCheckoutOptions = {
    buttonHeight: buttonHeight || 44,
    layout: stripeLayout,
    buttonType: {
      applePay: "buy",
      googlePay: "buy"
    },
    paymentMethods: {
      applePay: "always",
      googlePay: "always"
    }
  };

  const expressCheckout = elements.create("expressCheckout", expressCheckoutOptions);
  expressCheckout.mount(mountElement);

  expressCheckout.on("click", event => {
    window.jiffyThreads.payments.selectedPaymentMethod = event.expressPaymentType;

    sendDataLayer({
      genericEvent: true,
      eventCategory: `Page: ${pageSource}`,
      event: `click: checkout: ${event.expressPaymentType}`,
      eventLabel: userType
    });

    startExpressCheckout(event, elements, getContext);
  });

  expressCheckout.on("shippingaddresschange", event => {
    handleShippingAddressChange(event, elements, getContext);
  });

  expressCheckout.on("confirm", event => {
    handleConfirm(event, stripe, elements, getContext);
  });

  expressCheckout.on("cancel", () => {
    paymentOverlay.hide();

    sendDataLayer({
      genericEvent: true,
      eventCategory: `Page: ${pageSource}`,
      event: `Cancel: checkout: ${window.jiffyThreads.payments.selectedPaymentMethod}`,
      eventLabel: userType
    });

    logCheckoutData(
      orderNumber,
      "Stripe",
      `${window.jiffyThreads.payments.selectedPaymentMethod}.user_cancel`
    );
  });
};
